import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../ui/global/Colors';
import Fonts from '../../ui/global/Fonts';
import InputSimple from '../../ui/components/InputSimple';
import Button from '../../ui/components/Button';
import { useUser } from '../../backend/useUser';

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.black};
    color: ${Colors.white};
    overflow-y: auto;
    overflow-x: hidden;
    padding: 5px;
    gap: 10px;
`;

const Top = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 780px;
    padding: 10px;
    gap: 20px;
`;

const Inner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 780px;
    flex: 1;
    min-height: 0;
    padding: 10px;
    gap: 20px;
`;

const Title = styled.h2`
    font-family: ${Fonts.lexendBold.fontFamily};
    font-weight: ${Fonts.lexendBold.fontWeight};
    font-size: 36px;
    line-height: 1.2em;
    color: ${Colors.white};
    margin: 20px 0 10px 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
`;

const Section = styled.div<{ $alignItems?: 'flex-start' | 'center' | 'flex-end' }>`
    display: flex;
    flex-direction: column;
    align-items: ${({ $alignItems }) => $alignItems || 'flex-start'};
    width: 100%;
    gap: 10px;
`;

const SectionRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: 20px;

    @media (max-width: 780px) {
        flex-direction: column;
    }
`;

const SectionTitle = styled.h2`
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 18px;
    line-height: 1.2em;
    color: ${Colors.grey2};
    margin: 0;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: 10px;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
    border-radius: 4px;
    padding: 10px;
    transition: border 0.2s ease;

    &:hover {
        border: 1px solid ${Colors.grey7};
    }
`;

const PhotoContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 160px;
    height: 160px;
    border-radius: 100%;
    overflow: hidden;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
    cursor: pointer;
    margin: 0 10px 30px 10px;
    transition: border 0.2s ease;

    &:hover {
        border: 1px solid ${Colors.grey7};
    }
`;

const PhotoImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PhotoUploadInput = styled.input`
    display: none;
`;

const LinkList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
`;

const LinkItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    gap: 10px;
`;

const LinkButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: fit-content;
    margin: auto;
`;

interface Link {
    id: string;
    url: string;
}

const Profile = () => {
    const { user } = useUser();
    const [name, setName] = useState('');
    const [username, setUsername] = useState('');
    const [photoUrl, setPhotoUrl] = useState('/icons/icon-person-fill.svg');
    const [links, setLinks] = useState<Link[]>([]);
    const [bio, setBio] = useState('');

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setUsername('@' + user.user_tag || '');

            // TODO: Load from user profile
            // setPhotoUrl(user.photoUrl || '');
            // setBio(user.bio || '');
            setLinks([{ id: Date.now().toString(), url: '' }]);
        }
    }, [user]);

    const handleNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setName(e.target.value);
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let value = e.target.value;
        if (!value.startsWith('@')) {
            value = '@' + value;
        }
        setUsername(value);
    };

    const handlePhotoClick = () => {
        document.getElementById('photo-upload')?.click();
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // TODO: Implement photo upload
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBio(e.target.value);
    };

    const addLink = () => {
        setLinks([...links, { id: Date.now().toString(), url: '' }]);
    };

    const updateLink = (id: string, url: string) => {
        setLinks(links.map(link => 
            link.id === id ? { ...link, url } : link
        ));
    };

    const removeLink = (id: string) => {
        setLinks(links.filter(link => link.id !== id));
    };

    const handleSave = () => {
        // TODO: Implement save functionality
        // console.log('Saving profile:', { name, username, photoUrl, links });
    };

    return (
        <Root>
            <Top>
                <Title>Profile</Title>
                <ButtonContainer>
                    <Button
                        size="medium"
                        color="ghost white"
                        icon="delete"
                        onClick={handleSave}
                    />
                </ButtonContainer>
            </Top>

            <Inner>
                <Section $alignItems="center">
                    {/* <SectionTitle>Photo</SectionTitle> */}
                    <PhotoContainer onClick={handlePhotoClick}>
                        {photoUrl ? (
                            <PhotoImage src={photoUrl} alt="Profile" />
                        ) : (
                            <Button 
                                size="large" 
                                color="ghost white" 
                                icon="plus"
                                onClick={()=>{}}
                            />
                        )}
                    </PhotoContainer>
                    <PhotoUploadInput
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                    />
                </Section>

                <SectionRow>
                    <Section>
                        <SectionTitle>Name</SectionTitle>
                        <InputContainer>
                            <InputSimple
                                value={name}
                                onChange={handleNameChange}
                                placeholder="Your name"
                                colorMode="dark"
                                textAlign="left"
                            />
                        </InputContainer>
                    </Section>

                    <Section>
                        <SectionTitle>Tag</SectionTitle>
                        <InputContainer>
                            <InputSimple
                                value={username}
                                onChange={handleUsernameChange}
                                placeholder="@username"
                                colorMode="dark"
                                textAlign="left"
                            />
                        </InputContainer>
                    </Section>
                </SectionRow>

                {/* <Section>
                    <SectionTitle>Name</SectionTitle>
                    <InputContainer>
                        <InputSimple
                            value={name}
                            onChange={handleNameChange}
                            placeholder="Your name"
                            colorMode="dark"
                            textAlign="left"
                        />
                    </InputContainer>
                </Section>

                <Section>
                    <SectionTitle>Username</SectionTitle>
                    <InputContainer>
                        <InputSimple
                            value={username}
                            onChange={handleUsernameChange}
                            placeholder="@username"
                            colorMode="dark"
                            textAlign="left"
                        />
                    </InputContainer>
                </Section> */}

                <Section>
                    <SectionTitle>Bio</SectionTitle>
                    <InputContainer>
                        <InputSimple
                            value={bio}
                            onChange={handleBioChange}
                            colorMode="dark"
                            textAlign="left"
                        />
                    </InputContainer>
                </Section>

                <Section>
                    <SectionTitle>Links</SectionTitle>
                    <LinkList>
                        {links.map(link => (
                            <LinkItem key={link.id}>
                                <InputContainer style={{ flex: 1 }}>
                                    <InputSimple
                                        value={link.url}
                                        onChange={(e) => updateLink(link.id, e.target.value)}
                                        placeholder="https://"
                                        colorMode="dark"
                                        textAlign="left"
                                    />
                                    <Button
                                        size="tiny"
                                        color="ghost white"
                                        icon="delete"
                                        onClick={() => removeLink(link.id)}
                                    />
                                </InputContainer>
                            </LinkItem>
                        ))}
                        <LinkButtonContainer>
                            <Button
                                size="medium"
                                color="ghost white"
                                icon="plus"
                                onClick={addLink}
                            />
                        </LinkButtonContainer>
                    </LinkList>
                </Section>

            </Inner>
        </Root>
    );
};

export default Profile;